import {
    Banner,
    Button,
    Checkbox,
    COUNTRY_CODES,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ValidatingInput,
    ValidatingSelect,
    ValidationForm,
    ValidationSummary,
} from '@ygtechnologies/bygr-frontend-component-library';
import theme from './EditProfileModal.module.scss';
import React, { useState } from 'react';
import { addProfile, addProfileOptions, checkProfileName, setProfileSettings } from 'services/profiles';
import { useAsyncState } from 'utils/hooks';
import { getCurrencies } from 'services/currencies';
import { BygrProfileDetails, UnitOfMeasurement } from 'types';

interface EditProfileModalProps {
    isOpen: boolean;
    profile: BygrProfileDetails;
    toggle(): void;
    onSave(): void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, profile, toggle, onSave }) => {
    const [profileOptions, setProfileOptions] = useState<
        Pick<BygrProfileDetails, 'countryCode' | 'defaultUnitOfMeasurement'> & { currencyId: number }
    >({
        currencyId: profile.currency?.id ?? 1,
        countryCode: profile.countryCode,
        defaultUnitOfMeasurement: profile.defaultUnitOfMeasurement,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [skipNameValidation, setSkipNameValidation] = useState(false);
    const [similarProfileNames, setSimilarProfileNames] = useState<string[]>([]);
    const [currencies] = useAsyncState(() => getCurrencies(), [], null);

    const handleAddProfile = async () => {
        setIsSaving(true);
        try {
            await setProfileSettings(profile.id, {
                currencyId: profileOptions.currencyId,
                countryCode: profileOptions.countryCode,
            });
            onSave();
            toggle();
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (changes: Partial<addProfileOptions>, resetNameCheck: boolean = false) => {
        if (resetNameCheck) {
            setSkipNameValidation(false);
        }

        setProfileOptions((prevValue) => ({ ...prevValue, ...changes }));
    };

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader>Edit profile settings for {profile.name}</ModalHeader>
            <ValidationForm onSubmit={handleAddProfile}>
                <ModalBody className={theme.body}>
                    <div className="form columns-1">
                        <label>
                            Country
                            <select
                                className="form-control"
                                value={profileOptions.countryCode}
                                onChange={(e) =>
                                    handleChange({ countryCode: e.target.value as keyof typeof COUNTRY_CODES })
                                }>
                                {Object.entries(COUNTRY_CODES).map(([key, value]) => (
                                    <option key={key} value={key}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {currencies && (
                            <label>
                                Currency
                                <select
                                    className="form-control"
                                    value={profileOptions.currencyId}
                                    onChange={(e) => handleChange({ currencyId: +e.target.value })}>
                                    {currencies.map((x) => (
                                        <option key={x.id} value={x.id}>
                                            {x.name} ({x.code})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    <ValidationSummary />
                </ModalBody>
                <ModalFooter>
                    <Button onClick={toggle} color="tertiary">
                        Cancel
                    </Button>
                    <Button loading={isSaving} type="submit" color="primary">
                        Update settings
                    </Button>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default EditProfileModal;
